import React, { useState } from 'react';
import { Network, User, LogOut, ArrowLeft, Globe, ChevronDown } from 'lucide-react';
import type { User as UserType } from '../types';
import { useTranslation } from 'react-i18next';

interface CandidateLayoutProps {
  children: React.ReactNode;
  user: UserType;
  currentView: string;
  onViewChange: (view: any) => void;
  onLogout: () => void;
  onSwitchToAdmin?: () => void;
  isAdmin: boolean;
  onBack?: () => void;
  showBack?: boolean;
  title?: string;
}

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pl', label: 'Polski' },
];

export const CandidateLayout: React.FC<CandidateLayoutProps> = ({
  children,
  user,
  currentView,
  onViewChange,
  onLogout,
  onSwitchToAdmin,
  isAdmin,
  onBack,
  showBack,
  title,
}) => {
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <div className="flex flex-col min-h-screen bg-aero-50">
      {/* Top Header */}
      <header className="h-14 bg-aero-900 text-white flex items-center justify-between px-4 flex-shrink-0 shadow-md z-20">
        {/* Left: back button or logo */}
        <div className="flex items-center gap-3 w-32">
          {showBack && onBack ? (
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg hover:bg-aero-800 transition-colors flex items-center gap-1.5 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{t('common.back')}</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-green-500 rounded-md flex items-center justify-center text-white font-bold text-base shadow transform skew-x-[-10deg]">
                <span className="transform skew-x-[10deg]">S</span>
              </div>
              <span className="font-bold text-base tracking-tight hidden sm:block">SoloReady</span>
            </div>
          )}
        </div>

        {/* Centre: page title */}
        <div className="flex-1 text-center">
          {title && <span className="text-sm font-semibold text-white">{title}</span>}
        </div>

        {/* Right: language + user menu */}
        <div className="flex items-center gap-2 w-32 justify-end">
          {/* Language selector */}
          <div className="relative">
            <button
              onClick={() => { setShowLangMenu(!showLangMenu); setShowUserMenu(false); }}
              className="p-1.5 rounded-lg hover:bg-aero-800 transition-colors flex items-center gap-1 text-xs text-aero-300"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{currentLang.code.toUpperCase()}</span>
            </button>
            {showLangMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-aero-100 py-1 z-50 min-w-[140px]">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => { i18n.changeLanguage(lang.code); setShowLangMenu(false); }}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-aero-50 transition-colors ${i18n.language === lang.code ? 'text-aero-600 font-medium' : 'text-aero-700'}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => { setShowUserMenu(!showUserMenu); setShowLangMenu(false); }}
              className="flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-aero-800 transition-colors"
            >
              <div className="w-6 h-6 rounded-full bg-aero-700 flex items-center justify-center text-white text-xs font-bold">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <ChevronDown className="w-3 h-3 text-aero-400" />
            </button>
            {showUserMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-aero-100 py-1 z-50 min-w-[180px]">
                <div className="px-4 py-2 border-b border-aero-100">
                  <p className="text-sm font-semibold text-aero-900">{user.name}</p>
                  <p className="text-xs text-aero-400 truncate">{user.email}</p>
                </div>
                {isAdmin && onSwitchToAdmin && (
                  <button
                    onClick={() => { onSwitchToAdmin(); setShowUserMenu(false); }}
                    className="w-full px-4 py-2 text-sm text-left text-aero-700 hover:bg-aero-50 flex items-center gap-2 transition-colors"
                  >
                    <Network className="w-4 h-4 text-aero-400" />
                    Switch to Admin
                  </button>
                )}
                <button
                  onClick={onLogout}
                  className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  {t('common.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto pb-20">
        {children}
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-aero-200 flex items-center z-20 shadow-lg">
        <button
          onClick={() => onViewChange('my-pathways')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 transition-colors ${
            currentView === 'my-pathways' || currentView === 'pathway-detail' || currentView === 'block-detail' || currentView === 'ojt-session'
              ? 'text-green-600'
              : 'text-aero-400 hover:text-aero-600'
          }`}
        >
          <Network className={`w-5 h-5 ${currentView !== 'profile' ? 'text-green-500' : 'text-aero-400'}`} />
          <span className="text-[10px] font-medium">{t('candidate.nav.myPathways')}</span>
        </button>
        <button
          onClick={() => onViewChange('profile')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 transition-colors ${
            currentView === 'profile' ? 'text-green-600' : 'text-aero-400 hover:text-aero-600'
          }`}
        >
          <User className={`w-5 h-5 ${currentView === 'profile' ? 'text-green-500' : 'text-aero-400'}`} />
          <span className="text-[10px] font-medium">{t('candidate.nav.profile')}</span>
        </button>
      </nav>
    </div>
  );
};
